import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import type { Order } from '../types/order';
import { formatCurrency, formatDateTime, formatStatus } from '../utils/formatters';

interface OrderDetailsModalProps {
  order: Order | null;
  visible: boolean;
  onClose: () => void;
  type: 'new' | 'accepted';
  onStatusUpdate: (orderId: string, status: string) => Promise<void>;
}

export const OrderDetailsModal = observer(({
  order,
  visible,
  onClose,
  type,
  onStatusUpdate,
}: OrderDetailsModalProps) => {
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  if (!order || !visible) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-amber-100 text-amber-800';
      case 'CONFIRMED':
        return 'bg-blue-100 text-blue-800';
      case 'PREPARING':
        return 'bg-blue-100 text-blue-800';
      case 'READY':
        return 'bg-purple-100 text-purple-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleActionWithLoading = async (action: string, status: string) => {
    setLoadingAction(action);
    try {
      await onStatusUpdate(order.id, status);
      onClose();
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  // Parse items if they're a JSON string
  const parsedItems = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
  const itemsArray = Array.isArray(parsedItems) ? parsedItems : [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50">
      <div className="bg-gray-50 rounded-t-3xl max-h-[90vh] w-full max-w-2xl flex flex-col">
        {/* Header */}
        <div className="bg-white px-6 py-4 border-b border-gray-200 rounded-t-3xl flex-shrink-0">
          <div className="flex flex-row justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Order Details</h2>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
            >
              <span className="text-gray-600 text-2xl leading-none">×</span>
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-4 flex-1">
          {/* Order Header Info */}
          <div className="bg-white rounded-xl p-4 my-4">
            <p className="text-xl font-bold text-gray-900 mb-1">
              #{order.order_reference}
            </p>
            <p className="text-sm text-gray-500 mb-3">{formatDateTime(order.created_at)}</p>
            <div className={`self-start px-3 py-1.5 rounded-full ${getStatusColor(order.status)}`}>
              <span className="text-xs font-semibold">{formatStatus(order.status)}</span>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-white rounded-xl p-4 mb-4">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Customer Information</h3>

            <div>
              <div className="mb-3">
                <p className="text-sm text-gray-500 mb-1">Name</p>
                <p className="text-base text-gray-900">
                  {order.customer_name || 'Walk-in Customer'}
                </p>
              </div>

              {order.customer_phone && (
                <div className="mb-3">
                  <p className="text-sm text-gray-500 mb-1">Phone</p>
                  <p className="text-base text-gray-900">{order.customer_phone}</p>
                </div>
              )}

              {order.customer_email && (
                <div className="mb-3">
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="text-base text-gray-900">{order.customer_email}</p>
                </div>
              )}

              <div className="mb-3">
                <p className="text-sm text-gray-500 mb-1">Order Type</p>
                <p className="text-base text-gray-900">
                  {order.order_type === 'DELIVERY'
                    ? '🚚 Delivery'
                    : order.order_type === 'DINE_IN'
                    ? '🍽️ Dine In'
                    : '🏪 Pickup'}
                </p>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-white rounded-xl p-4 mb-4">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              Order Items ({itemsArray.length})
            </h3>

            {itemsArray.map((item: any, index: number) => {
              // Calculate item total: use provided total or calculate from price * quantity
              const itemTotal = item.total && item.total > 0
                ? item.total
                : (item.price || 0) * (item.quantity || 1);

              return (
                <div
                  key={index}
                  className={`py-3 ${index < itemsArray.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <div className="flex flex-row justify-between items-start mb-1">
                    <div className="flex-1 mr-2">
                      <p className="text-base text-gray-900 font-medium line-clamp-2">
                        {item.name}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {formatCurrency(item.price)} × {item.quantity}
                      </p>
                    </div>
                    <p className="text-base font-bold text-gray-900">
                      {formatCurrency(itemTotal)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="bg-white rounded-xl p-4 mb-4">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Order Summary</h3>

            <div>
              <div className="flex flex-row justify-between items-center py-2">
                <p className="text-base text-gray-600">Subtotal</p>
                <p className="text-base text-gray-900">{formatCurrency(order.subtotal)}</p>
              </div>

              {order.tax > 0 && (
                <div className="flex flex-row justify-between items-center py-2">
                  <p className="text-base text-gray-600">Tax</p>
                  <p className="text-base text-gray-900">{formatCurrency(order.tax)}</p>
                </div>
              )}

              <div className="flex flex-row justify-between items-center py-2 border-t border-gray-200 mt-2">
                <p className="text-xl font-bold text-gray-900">Total</p>
                <p className="text-xl font-bold text-[#00B2A8]">{formatCurrency(order.total)}</p>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white rounded-xl p-4 mb-4">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Payment</h3>

            <div>
              <div className="flex flex-row justify-between items-center mb-3">
                <p className="text-sm text-gray-500">Payment Status</p>
                <div className={`px-3 py-1 rounded-full ${
                  order.payment_status === 'PAID' ? 'bg-green-100' :
                  order.payment_status === 'PENDING' ? 'bg-amber-100' :
                  order.payment_status === 'FAILED' ? 'bg-red-100' : 'bg-gray-100'
                }`}>
                  <span className={`text-xs font-semibold ${
                    order.payment_status === 'PAID' ? 'text-green-800' :
                    order.payment_status === 'PENDING' ? 'text-amber-800' :
                    order.payment_status === 'FAILED' ? 'text-red-800' : 'text-gray-800'
                  }`}>
                    {formatStatus(order.payment_status)}
                  </span>
                </div>
              </div>

              <div className="flex flex-row justify-between items-center">
                <p className="text-sm text-gray-500">Payment Method</p>
                <p className="text-base text-gray-900">
                  {formatStatus(order.payment_mode || 'N/A')}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {type === 'new' && (
            <div className="flex flex-row gap-x-3 mb-4">
              <button
                onClick={() => handleActionWithLoading('cancel', 'CANCELLED')}
                className="bg-[#DC2626] flex-1 py-3 rounded-xl disabled:opacity-50"
                disabled={!!loadingAction}
              >
                {loadingAction === 'cancel' ? (
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  </div>
                ) : (
                  <span className="text-white text-center font-semibold text-base">Cancel Order</span>
                )}
              </button>
              <button
                onClick={() => handleActionWithLoading('confirm', 'CONFIRMED')}
                className="bg-[#16A34A] flex-1 py-3 rounded-xl disabled:opacity-50"
                disabled={!!loadingAction}
              >
                {loadingAction === 'confirm' ? (
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  </div>
                ) : (
                  <span className="text-white text-center font-semibold text-base">Confirm Order</span>
                )}
              </button>
            </div>
          )}

          {type === 'accepted' && (
            <div className="flex flex-row gap-x-3 mb-4">
              {order.status === 'CONFIRMED' && (
                <button
                  onClick={() => handleActionWithLoading('preparing', 'PREPARING')}
                  className="bg-[#2196F3] flex-1 py-3 rounded-xl disabled:opacity-50"
                  disabled={!!loadingAction}
                >
                  {loadingAction === 'preparing' ? (
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    </div>
                  ) : (
                    <span className="text-white text-center font-semibold text-base">Start Preparing</span>
                  )}
                </button>
              )}

              {order.status === 'PREPARING' && (
                <button
                  onClick={() => handleActionWithLoading('ready', 'READY')}
                  className="bg-[#9C27B0] flex-1 py-3 rounded-xl disabled:opacity-50"
                  disabled={!!loadingAction}
                >
                  {loadingAction === 'ready' ? (
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    </div>
                  ) : (
                    <span className="text-white text-center font-semibold text-base">Mark Ready</span>
                  )}
                </button>
              )}

              {order.status === 'READY' && (
                <button
                  onClick={() => handleActionWithLoading('complete', 'COMPLETED')}
                  className="bg-green-600 flex-1 py-3 rounded-xl disabled:opacity-50"
                  disabled={!!loadingAction}
                >
                  {loadingAction === 'complete' ? (
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    </div>
                  ) : (
                    <span className="text-white text-center font-semibold text-base">Mark Complete</span>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
